define((require) => {
    "use strict";

    const $                  = require("jquery");
    const Promise            = require("shared/util/Promise");
    const AbstractWidgetView = require("shared/ui/experience/widgets/AbstractWidgetView");
    const ImageURLProvider   = require("shared/ui/experience/widgets/imageWidget/ImageURLProvider");
    const StyleableMixin     = require("shared/ui/experience/widgets/StyleableMixin");
    const LoggedInUserState  = require("shared/ui/user/LoggedInUserState");
    /**
     * The view rendering an image widget.
     *
     * @cfg {ImageWidgetModel} model The model of the image widget being rendered.
     *
     * @class ImageWidgetView
     *
     * @see ImageWidgetViewEditorMixin
     */
    return AbstractWidgetView.extend("ImageWidget", {

        className: `${AbstractWidgetView.prototype.className} image-widget`,

        mixin: StyleableMixin,

        /**
         * The (jQuery) element representing the image.
         *
         * @type {jQuery} jQuery wrapped <tt>HTMLImageElement</tt>.
         */
        _$imageElement: null,

        /**
         * A reference to the image URL, used for state handling.
         *
         * @see #updateRendering
         *
         * @type {String}
         */
        _source: null,

        /**
         * The current image URL, provided by the <tt>ImageURLProvider</tt>.
         *
         * @see #updateRendering
         *
         * @type {String}
         */
        _currentUrl: null,

        /**
         * @type {$.Deferred}
         */
        _imageFetchDeferred: null,

        /**
         * The image base url of the third party image library integration
         *
         * @type {String}
         */
        _imageServingUrl: null,

        /**
         * Image provider settings has been loaded
         *
         * @type {Boolean}
         */
        _imageProviderLoaded: null,

        events: {
            // This prevents the default drag and drop behavior in the browser
            "dragstart": () => false
        },

        modelEvents: {
            "change": "render"
        },

        /**
         * @private
         *
         * Initializes the <tt>Deferred</tt> instance, and registers listeners
         * for <tt>load</tt> and <tt>error</tt> events.
         *
         * @param {jQuery} element The jQuery wrapped element.
         */
        onInitialize(element) {
            this._imageFetchDeferred = Promise.deferred();

            // Create the actual image element - appending it to this element/widget
            this._$imageElement = $("<img />")
                .one("load", this._imageFetchDeferred.resolve.bind(this, this))
                .one("error", () => {
                    this._renderOriginalImage();
                    // Log error
                    if (this.options && this.options.errorLogger) {
                        this.options.errorLogger.log(`Unable to load image widget URL: ${this._currentUrl}`);
                    }

                    this._imageFetchDeferred.reject(this);
                })
                .appendTo(element);

            // Check widget used in Viewer
            if (!LoggedInUserState.get("companySettings")
                // Check if CompanySettings for viewer already exists
                && (typeof this.options.viewModel.get("companySettings").get !== "function")) {
                // Add a listener, for provider options async load in CompanySettings change event
                this._imageProviderLoaded = false;
                this._listenToProviderReadyEvent();
            } else {
                this._imageProviderLoaded = true;
            }
        },

        updateRendering(element, bounds, active, edited, changedProperties) {
            if (this.model.get("sourceType")) {
                this._imageServingUrl = this._getImageServingUrl();
            }

            const altText = this.model.get("altText");
            const resource = this.model.get("imageUrl");
            const sizeChanged = changedProperties && (changedProperties.scaledWidth || changedProperties.scaledHeight);

            // Do not render image, if image provider is not loaded yet
            if ((this._source !== resource || sizeChanged) && this._imageProviderLoaded) {
                const provideOptions = {bounds};

                if (this.model.get("sourceType")) {
                    provideOptions.imageServingUrl = this._imageServingUrl;
                }

                // Check widget used in Viewer
                if (!LoggedInUserState.get("companySettings")) {
                    const companySettings = this.options.viewModel.get("companySettings");
                    if (companySettings && companySettings.has("cloudinaryDefaultImageProvider")) {
                        provideOptions.cloudinary = companySettings.get("cloudinaryDefaultImageProvider");
                    }
                } else {
                    // Editor or preview
                    const companySettings = LoggedInUserState.get("companySettings");
                    if (companySettings && companySettings.has("cloudinaryDefaultImageProvider")) {
                        provideOptions.cloudinary = companySettings.get("cloudinaryDefaultImageProvider");
                    }
                }

                this._currentUrl = ImageURLProvider.provide(this.model, provideOptions);
                this._$imageElement
                    .attr({
                        alt: altText,
                        src: this._currentUrl,
                        title: this.model.get("title")
                    })
                    .css({
                        position: "absolute",
                        width: "100%",
                        height: "100%"
                    });

                this._source = resource;
            }
        },

        _getImageServingUrl() {
            const experienceModel = this.options.viewModel.get("experience");
            const imageIntegration = experienceModel.getIntegration(this.model.get("sourceType"));
            return imageIntegration && imageIntegration.imageServingUrl;
        },

        _renderOriginalImage() {
            if (this.model.get("sourceType") && !this._imageServingUrl) {
                // The integration is missing, no need to retry the request without a base url
                return;
            }

            this._$imageElement.attr({
                src: ImageURLProvider.provide(this.model, {imageServingUrl: this._imageServingUrl})
            });
        },

        /**
         * Registers a listener for the `companySettings:change` event on the `viewModel`.
         */
        _listenToProviderReadyEvent() {
            this.listenTo(this.options.viewModel, "change:companySettings", () => {
                this._imageProviderLoaded = true;
                // Trigger widget rerender
                this.render();
            });
        }
    }, {
        // Inject the current error logger to log if image load fails.
        injection: {
            parameters: ["errorLogger"],
            asOptions: true
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/imageWidget/ImageWidgetView.js